import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Seo from "../components/Seo";

export default function RegisterPage() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Bitte Datenschutz-Hinweis bestätigen.");
            return;
        }
        if (password.length < 8) {
            toast.error("Passwort muss mindestens 8 Zeichen haben.");
            return;
        }
        setLoading(true);
        try {
            await register(email, password, name);
            toast.success("Konto erstellt – willkommen!");
            const intent = location.state?.intent;
            navigate(intent === "unlock-result" ? "/ergebnis" : "/konto", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Registrierung fehlgeschlagen."));
        } finally {
            setLoading(false);
        }
    };

    const onGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-[80vh] bg-brand-50/30 py-10">
            <Seo title="Kostenlos registrieren" description="Erstellen Sie in wenigen Sekunden Ihr kostenloses, vertrauliches Konto und sehen Sie Ihre vollständige Einschätzung sowie Ihr passendes Beratungspaket." path="/register" noIndex />
            <div className="container max-w-md">
                <div className="rounded-3xl border border-ink-300/30 bg-white p-7 shadow-softer">
                    <h1 className="font-display text-2xl font-bold">Kostenloses Konto erstellen</h1>
                    <p className="mt-1 text-sm text-ink-500">
                        Ergebnisse speichern, Empfehlungen sehen, sicher weitermachen.
                    </p>

                    <Button
                        type="button"
                        onClick={onGoogle}
                        variant="outline"
                        className="mt-6 h-12 w-full rounded-full gap-3 border-ink-300/40"
                        data-testid="register-google"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.3C29.1 35.1 26.7 36 24 36c-5.2 0-9.6-3.1-11.3-7.6l-6.6 5.1C9.6 39.7 16.2 44 24 44z"/>
                            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.8l6.2 5.3C41.2 35.5 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                        </svg>
                        Mit Google registrieren
                    </Button>

                    <div className="my-6 flex items-center gap-3 text-xs text-ink-400">
                        <span className="h-px flex-1 bg-ink-300/40" /> oder mit E-Mail <span className="h-px flex-1 bg-ink-300/40" />
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="name">Name (optional)</Label>
                            <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                                className="mt-1 h-12 rounded-xl" data-testid="register-name" />
                        </div>
                        <div>
                            <Label htmlFor="email">E-Mail</Label>
                            <Input id="email" type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 h-12 rounded-xl" data-testid="register-email" />
                        </div>
                        <div>
                            <Label htmlFor="password">Passwort</Label>
                            <Input id="password" type="password" required minLength={8} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 h-12 rounded-xl" data-testid="register-password" />
                            <p className="mt-1 text-xs text-ink-400">Mindestens 8 Zeichen.</p>
                        </div>

                        <label className="flex items-start gap-3 text-sm text-ink-700">
                            <Checkbox
                                checked={consent}
                                onCheckedChange={setConsent}
                                className="mt-1"
                                data-testid="register-consent"
                            />
                            <span>
                                Ich stimme zu, dass meine Angaben gemäß{" "}
                                <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>{" "}
                                verarbeitet werden.
                            </span>
                        </label>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="h-12 w-full rounded-full bg-brand-500 hover:bg-brand-600"
                            data-testid="register-submit"
                        >
                            {loading ? "Erstellen…" : "Kostenlos registrieren"}
                        </Button>
                    </form>

                    <p className="mt-6 text-center text-sm text-ink-500">
                        Bereits ein Konto?{" "}
                        <Link to="/login" className="font-semibold text-brand-700" data-testid="register-to-login">
                            Anmelden
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
